import React from "react";
import { Modal, Typography } from "antd";
import { AlertTriangle } from "lucide-react";
import { StatusPill } from "../../design-system/tableUi";
import { AN_COLORS, AN_MONO } from "../../design-system/tokens";
import { DIRECT_ACCESS, EXPOSED_WARNING, checkedText, directAccessKey } from "./dbAgentModel";

/** Direct-access pill plus "checked 2 min ago"; clicking opens the detail. */
export const DirectAccessBadge = ({ host, now, onOpen }) => {
  const key = directAccessKey(host?.directAccessStatus);
  const meta = DIRECT_ACCESS[key];
  const checked = checkedText(host?.directAccessCheckedAt, now);
  return (
    <button
      type="button"
      onClick={() => onOpen(host)}
      aria-label={`Direct access ${meta.label}. Show details`}
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "0.2rem",
        padding: 0,
        border: 0,
        background: "transparent",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <StatusPill status={key} label={meta.label} tone={meta.tone} />
      {checked && <span style={{ fontSize: "0.72rem", color: AN_COLORS.muted }}>{checked}</span>}
    </button>
  );
};

/** The agent's findings and the exact lines to fix, as copyable preformatted text. */
export const DirectAccessModal = ({ host, now, onClose }) => {
  const key = directAccessKey(host?.directAccessStatus);
  const meta = DIRECT_ACCESS[key];
  const detail = String(host?.directAccessDetail || "").trim();
  const checked = checkedText(host?.directAccessCheckedAt, now);
  return (
    <Modal
      open={!!host}
      onCancel={onClose}
      footer={null}
      width="44rem"
      title={`Direct access on ${host?.hostName || host?.agentHostAddress || "this host"}`}
      destroyOnClose
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
        <StatusPill status={key} label={meta.label} tone={meta.tone} />
        {checked && <span style={{ fontSize: "0.8rem", color: AN_COLORS.muted }}>{checked}</span>}
      </div>

      {key === "exposed" && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.5rem",
            padding: "0.5rem 0.75rem",
            marginBottom: "0.75rem",
            borderRadius: "0.5rem",
            border: "1px solid var(--an-dangerBd)",
            background: "var(--an-dangerBg)",
            color: "var(--an-dangerFg)",
            fontSize: "0.85rem",
          }}
        >
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: "0.1rem" }} />
          <span>{EXPOSED_WARNING}</span>
        </div>
      )}

      {detail ? (
        <div style={{ position: "relative" }}>
          <pre
            style={{
              margin: 0,
              padding: "0.75rem 2.5rem 0.75rem 0.75rem",
              maxHeight: "24rem",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: AN_MONO,
              fontSize: "0.78rem",
              lineHeight: 1.55,
              color: AN_COLORS.ink2,
              background: AN_COLORS.tint,
              border: `1px solid ${AN_COLORS.line}`,
              borderRadius: "0.5rem",
            }}
          >
            {detail}
          </pre>
          <Typography.Text
            copyable={{ text: detail, tooltips: ["Copy", "Copied"] }}
            style={{ position: "absolute", top: "0.5rem", right: "0.6rem" }}
          />
        </div>
      ) : (
        <Typography.Text type="secondary">
          This agent has not reported a direct-access check yet. Agents that check report on their next sync.
        </Typography.Text>
      )}
    </Modal>
  );
};
